import { API_BASE } from '../config';
import { getAuthToken } from '../utils/authStorage';

// Helper function to get auth headers
const authHeaders = async () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Send a notification
export async function sendNotification(notificationData) {
  const res = await fetch(`${API_BASE}/notifications/send`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(notificationData)
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to send notification');
  }
  
  return res.json();
}

// Get notifications with filters
export async function getNotifications(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.skip !== undefined) queryParams.append('skip', params.skip);
  if (params.limit !== undefined) queryParams.append('limit', params.limit);
  if (params.category) queryParams.append('category', params.category);
  if (params.priority) queryParams.append('priority', params.priority);
  if (params.is_read !== undefined) queryParams.append('is_read', params.is_read);
  if (params.team) queryParams.append('team', params.team);
  
  const url = `${API_BASE}/notifications/?${queryParams.toString()}`;
  const res = await fetch(url, {
    headers: await authHeaders()
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to fetch notifications');
  }
  
  return res.json();
}

// Get unread notifications
export async function getUnreadNotifications() {
  const res = await fetch(`${API_BASE}/notifications/unread`, {
    headers: await authHeaders()
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to fetch unread notifications');
  }
  
  return res.json();
}

// Get a specific notification
export async function getNotification(notificationId) {
  const res = await fetch(`${API_BASE}/notifications/${notificationId}`, {
    headers: await authHeaders()
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to fetch notification');
  }
  
  return res.json();
}

// Mark notification as read
export async function markNotificationRead(notificationId) {
  const res = await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
    method: 'POST',
    headers: await authHeaders()
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to mark notification as read');
  }
  
  return res.json();
}

// Update a notification
export async function updateNotification(notificationId, updateData) {
  const res = await fetch(`${API_BASE}/notifications/${notificationId}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(updateData)
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to update notification');
  }
  
  return res.json();
}

// Delete a notification
export async function deleteNotification(notificationId) {
  const res = await fetch(`${API_BASE}/notifications/${notificationId}`, {
    method: 'DELETE',
    headers: await authHeaders()
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to delete notification');
  }
  
  return res.json();
}

// Get notification categories
export async function getNotificationCategories() {
  const res = await fetch(`${API_BASE}/notifications/categories/list`, {
    headers: await authHeaders()
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to fetch notification categories');
  }
  
  return res.json();
}

// Get notification priorities
export async function getNotificationPriorities() {
  const res = await fetch(`${API_BASE}/notifications/priorities/list`, {
    headers: await authHeaders()
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to fetch notification priorities');
  }
  
  return res.json();
}